import React, { useEffect } from "react";
import { motion } from 'framer-motion';
import { FileText, File, Image, UserCheck, Download } from 'lucide-react';
import logo from '/img/logo.png';

export default function DocumentsRequired({ 
  accent = "#115e32",
  pageTitle = "Documents Required for Admission | University of Creative Technology Chittagong",
}) {
	useEffect(() => {
		document.title = pageTitle;
	}, [pageTitle]);
  const docs = [
    { title: 'SSC', items: ['Certificate', 'Transcript'], icon: <FileText size={28} /> },
    { title: 'HSC', items: ['Certificate', 'Transcript'], icon: <FileText size={28} /> },
    { title: 'Diploma', items: ['Certificate', 'Transcript'], icon: <File size={28} /> },
    { title: 'BBA / Honors / Pass Course', items: ['Certificate', 'Transcript'], icon: <FileText size={28} /> },
    { title: 'Photocopy of NID / Birth Certificate / Passport', items: [], icon: <Image size={28} /> },
    { title: 'Original Academic Documents', items: ['SSC + HSC (Mandatory for Regular Students)'], icon: <UserCheck size={28} /> },
    { title: 'Passport Photos', items: ['4 Copies passport size photographs with name written on the reverse'], icon: <Image size={28} /> },
    { title: 'Experience Certificate', items: ['Current + Active (For Masters Program)'], icon: <File size={28} /> },
  ];

  return (
    <section className="min-h-screen bg-gradient-to-b from-[#B7EAFC] via-[#F0FCFF] to-[#F0FCFF] flex flex-col items-center px-6 md:px-12 py-16">
	  
	  {/* Hero */}
	  <motion.header
		initial={{ opacity: 0, y: -20 }}
		animate={{ opacity: 1, y: 0 }}
		transition={{ duration: 0.8 }}
		className="w-full max-w-5xl text-center mb-12"
	  >
		<img src={logo} alt="UCTC Logo" className="mx-auto w-24 h-auto mb-4" />
		<h1 className="text-4xl md:text-5xl font-extrabold text-gray-900 mb-3">Documents Required for Admission</h1>
		<p className="text-gray-600 max-w-2xl mx-auto">Prepare the following documents before you submit your application. Use the checklist to make sure you don’t miss anything.</p>

		{/* Hero SVG accent */}
		<motion.svg
		  viewBox="0 0 800 160"
		  className="w-full h-40 mt-8 opacity-90"
		  initial={{ opacity: 0, y: 10 }}
		  animate={{ opacity: 1, y: 0 }}
		  transition={{ duration: 1.2 }}
		>
		  <defs>
			<linearGradient id="gA" x1="0" x2="1">
			  <stop offset="0%" stopColor="#b88f18" />
			  <stop offset="100%" stopColor="#7c5b0e" />
			</linearGradient>
		  </defs>
		  <g>
			<rect x="20" y="20" rx="18" width="220" height="120" fill="#fff" stroke="#f3e2b8" />
			<rect x="280" y="10" rx="18" width="300" height="140" fill="#fff" stroke="#dbeadf" />
			<path d="M640 40 q40 30 80 0" stroke="url(#gA)" strokeWidth="8" fill="none" strokeLinecap="round" />
			<motion.circle cx="140" cy="80" r="10" fill="url(#gA)" animate={{ cy: [80, 70, 80] }} transition={{ duration: 2.8, repeat: Infinity }} />
			<motion.circle cx="420" cy="70" r="8" fill="#8fb9a8" animate={{ cy: [70, 90, 70] }} transition={{ duration: 3.4, repeat: Infinity }} />
			<motion.circle cx="540" cy="100" r="6" fill="#b88f18" animate={{ cy: [100, 85, 100] }} transition={{ duration: 3.0, repeat: Infinity }} />
		  </g>
		</motion.svg>
	  </motion.header>
		
      {/* Checklist */}
      <main className="w-full max-w-5xl grid grid-cols-1 md:grid-cols-2 gap-6">
        {docs.map((doc, idx) => (
          <motion.article
            key={idx}
            whileHover={{ scale: 1.03 }}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: idx * 0.06 }}
            className="bg-white rounded-2xl p-6 shadow-md border hover:shadow-xl"
          >
            <div className="flex items-center gap-4 mb-3">
              <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-[#6BCCF2] to-[#C6F0FD] flex items-center justify-center text-[#015A7A]">
                {doc.icon}
              </div>
              <h3 className="text-lg font-semibold text-gray-900">{doc.title}</h3>
            </div>

            {doc.items.length ? (
              <ul className="text-gray-700 list-disc list-inside space-y-2">
                {doc.items.map((it, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <span className="inline-flex items-center justify-center w-6 h-6 bg-green-50 text-green-600 rounded-full mt-1">✓</span>
                    <span>{it}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-gray-600">Please provide a clear photocopy as applicable.</p>
            )}
          </motion.article>
        ))}

        {/* Checklist actions card */}
        <motion.aside
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="col-span-1 md:col-span-2 bg-gradient-to-r from-[#6BCCF2] to-teal-50 rounded-2xl p-6 shadow-lg border"
        >
          <div className="flex items-center justify-between mb-4">
            <div>
              <h4 className="text-xl font-bold text-gray-900">Complete Documents Checklist</h4>
              <p className="text-gray-700">Download a printable checklist to tick off before submission.</p>
            </div>
            <button className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-yellow-500 text-white font-semibold shadow" aria-label="Download checklist">
              <Download size={18} />
              Download Checklist
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="p-4 bg-white rounded-lg border flex items-start gap-3">
              <div className="text-yellow-600 mt-1">✓</div>
              <div>
                <div className="text-sm font-semibold">Certified copies of Academic Results</div>
                <div className="text-xs text-gray-600">SSC / HSC / Diploma / BBA/Honors/Pass</div>
              </div>
            </div>

            <div className="p-4 bg-white rounded-lg border flex items-start gap-3">
              <div className="text-yellow-600 mt-1">✓</div>
              <div>
                <div className="text-sm font-semibold">Photocopy of NID / Birth Certificate / Passport</div>
                <div className="text-xs text-gray-600">One clear copy required</div>
              </div>
            </div>

            <div className="p-4 bg-white rounded-lg border flex items-start gap-3">
              <div className="text-yellow-600 mt-1">✓</div>
              <div>
                <div className="text-sm font-semibold">Original Academic Documents</div>
                <div className="text-xs text-gray-600">SSC + HSC (Mandatory for Regular Students)</div>
              </div>
            </div>

            <div className="p-4 bg-white rounded-lg border flex items-start gap-3">
              <div className="text-yellow-600 mt-1">✓</div>
              <div>
                <div className="text-sm font-semibold">4 Copies Passport size photographs</div>
                <div className="text-xs text-gray-600">Write name on reverse</div>
              </div>
            </div>

            <div className="p-4 bg-white rounded-lg border flex items-start gap-3">
              <div className="text-yellow-600 mt-1">✓</div>
              <div>
                <div className="text-sm font-semibold">Experience Certificate (For Masters)</div>
                <div className="text-xs text-gray-600">Current & Active if applicable</div>
              </div>
            </div>

            <div className="p-4 bg-white rounded-lg border flex items-start gap-3">
              <div className="text-yellow-600 mt-1">!</div>
              <div>
                <div className="text-sm font-semibold text-red-600">Note</div>
                <div className="text-xs text-gray-600">GED results will not be accepted.</div>
              </div>
            </div>
          </div>
        </motion.aside>
      </main>
    </section>
  );
}
